import * as THREE from "three";
import { gsap, Power2, Power4 } from "gsap";
import { EventEmitter } from 'events';
import { transformPropKeys } from "../constants";
import isMobile from 'is-mobile';

export class World extends EventEmitter {
    constructor({ name, model, assets }) {
        super();
        this._debug = false;
        this.name = name;
        this.assets = assets;
        this.model = this.assets[model];
        this.scene = new THREE.Scene();
        this.bgrColor = 0x000000;
        this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 100);
        this.camera.position.set(0, 0, 50);
        this.scene.add(this.camera)

        if (this._debug) {
            // checker
            const geom = new THREE.BoxGeometry(2, 2, 2);
            const mat = new THREE.MeshBasicMaterial({ color: 0xff0000 })
            this.cubeChecker = new THREE.Mesh(geom, mat)
            this.cubeChecker.position.z = -10
            this.camera.add(this.cubeChecker)
        }

        // Steps
        this.cameras = [];
        this.targets = [];
        this._currentStep = 0;
        this._previousStep = 0; // Watch out for conflict with the function previousStep!
        this.tempTarget = new THREE.Vector3();
        this.isMovingToStep = false;
        this.transitionToStepProgress = 0;

        // Camera movements
        this.floatingHeadEnabled = true
        this.amplitudeHead = .1
        this.headTransitionSpeed = 1

        // Portal info
        this.renderTarget = new THREE.WebGLRenderTarget(2048, 2048, {
            type: THREE.HalfFloatType//isMobile.any ? THREE.HalfFloatType : THREE.FloatType,
        });
        this.portal = null;
        this.allowRender = true;

        this.mouse = new THREE.Vector2();
        this.raycaster = new THREE.Raycaster();

        this.titleInvert = false

        this.isFound = false
        this.isMarked = false

        this.processModel()
    }

    setControls(controls) {
        this.controls = controls;
    }

    processModel() {
        this.container = this.model.scene;
        this.scene.add(this.container);

        // Sort model cameras by the number part of their name (Camera1, Camera2 etc) zero indexed)
        this.cameras = this.model.cameras.sort((a, b) => /\d+/.exec(a.name)[0] - /\d+/.exec(b.name)[0]);

        // Corresponding targets
        this.targets = this.cameras.map((_, i) => this.scene.getObjectByName(`target${i}`));

        if (this._debug) {
            // target checker
            const geomchecker = new THREE.BoxGeometry();
            const matChecker = new THREE.MeshBasicMaterial({ color: "red" })
            this.camTargetChecker = new THREE.Mesh(geomchecker, matChecker)
            this.camTargetChecker.position.copy(this.tempTarget);
            this.scene.add(this.camTargetChecker);
            this.camTargetChecker.visible = false;
        }
    }

    setPortalTransforms(ts, te) {
        this.transformStart = ts;
        this.transformEnd = te;

        transformPropKeys.forEach(
            prop => this.container[prop].copy(this.transformStart[prop])
        );
    }

    resetTransform() {
        transformPropKeys.forEach(key => {
            const { x, y, z } = this.transformEnd[key];
            gsap.to(this.container[key], { x, y, z, duration: 2, ease: Power2.easeIn });
        });
    }

    animateToPortalTransforms(params) {
        const ease = Power2.easeOut;
        const duration = (params && params.duration) || 6;

        // loop through position / scale / rotation
        transformPropKeys.forEach(key => {
            const { x, y, z } = this.transformStart[key];
            const tween = gsap.to(this.container[key], { x, y, z, duration, ease });
            tween.progress(0.3);
            tween.play();
        });
    }

    resize(winWidth, winHeight) {
        this.camera.aspect = winWidth / winHeight;
        this.camera.updateProjectionMatrix();
    }

    loop({ time, deltaTime }) {

        if (this.transitionToStepProgress === 1 && this.floatingHeadEnabled) {

            const distToTarget = this.currentTargetPos.distanceTo(this.camera.position)

            const sin6 = Math.sin(time * 0.26);
            const cos4 = Math.cos(time * 0.27);
            const sin7 = Math.cos(time * 0.23);

            // Target
            // Multiply all by this adjustment
            const f = this.amplitudeHead * distToTarget * 0.5;

            const headMov = new THREE.Vector3(sin6 * f, cos4 * f, sin7 * f);

            const lookTarget = new THREE.Vector3(this.mouse.x, this.mouse.y, 0);
            lookTarget.multiplyScalar(this.amplitudeHead * distToTarget)
            lookTarget.add(headMov);
            lookTarget.applyQuaternion(this.camera.quaternion);
            lookTarget.add(this.currentTargetPos);

            // reducing the sudden movement on transition 1

            this.tempTarget.lerp(lookTarget, deltaTime * this.headTransitionSpeed);
            this.camera.lookAt(this.tempTarget)
        }

    }

    mouseMove(mousePos) {
        this.mouse.x = mousePos.px;
        this.mouse.y = - mousePos.py;
    }

    mouseDown() {
    }

    mouseUp() {
    }

    raycast() {
        this.raycaster.setFromCamera(this.mouse, this.camera);
    }

    previousStep() {
        return this.incrementStep(-1);
    }

    nextStep() {
        return this.incrementStep(1);
    }

    incrementStep(inc) {
        this._previousStep = this._currentStep;
        this._currentStep = (this._currentStep + inc + this.steps) % this.steps;
        this.updateStep();

        return this._currentStep;
    }

    updateStep(p) {
        let _this = this;
        const params = p || {};
        const exitFromWorld = params.exitFromWorld || false;
        const enterToWorld = params.enterToWorld || false;
        const defaultEase = params.ease || Power4.easeInOut;

        this.onUpdateStepStart(this._currentStep, exitFromWorld)

        const camPos = this.container.localToWorld(this.cameras[this._currentStep].position.clone());
        this.currentTarget = this.targets[this._currentStep];
        this.currentTargetPos = this.container.localToWorld(this.currentTarget.position.clone());
        const ease = (exitFromWorld) ? Power2.easeOut : (enterToWorld) ? Power2.easeOut : defaultEase;
        const duration = 3;

        gsap.killTweensOf(this.camera.position)
        gsap.killTweensOf(this.camera.up)

        gsap.to(this.tempTarget, {
            x: this.currentTargetPos.x,
            y: this.currentTargetPos.y,
            z: this.currentTargetPos.z,
            duration,
            ease,
        })

        this.camUp = new THREE.Vector3(0, 1, 0).applyQuaternion(this.currentTarget.quaternion)


        gsap.to(this.camera.up, {
            ...this.camUp,
            duration,
            //delay,
            ease
          })

        gsap.to(this.camera.position, {
            x: camPos.x,
            y: camPos.y,
            z: camPos.z,
            duration,
            ease,

            onUpdate: function () {
                _this.transitionToStepProgress = this.progress();
                _this.camera.lookAt(_this.tempTarget);

                if (_this.controls) {
                    _this.controls.target = _this.tempTarget;
                }
            },
            onComplete: () => {
                this.onUpdateStepComplete(this._currentStep)
            }
        })
    }

    onUpdateStepComplete() {
        this.isMovingToStep = false
        if (this.controls) {
            this.controls.enabled = true;
        }
    }
    onUpdateStepStart() {
        this.isMovingToStep = true
        this.transitionToStepProgress = 0
        if (this.controls) this.controls.enabled = false
    }

    get steps() {
        return this.cameras.length;
    }

    get debug() {
        return this._debug;
    }


    set currentStep(value) {
        this._currentStep = value;
    }

    get currentStep() {
        return this._currentStep;
    }
}